import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import apiClient from "@/api/client";

/** /cad-link (09-24): handoff page for FOFUS CAD Suite first-run login.
    Plugin opened a localhost receiver (127.0.0.1:7462) and this page delivers
    the JWT to it. If the receiver isn't reachable (plugin closed, firewall),
    fall back to showing a manual pairing code. */
export default function CadLink() {
  const [sp] = useSearchParams();
  const tool = sp.get("tool") || "blender";
  const [status, setStatus] = useState<"sending" | "linked" | "fallback">("sending");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // JWT from localStorage (apiClient stores it there after login)
      const jwt = localStorage.getItem("fofus_access_token") || "";
      if (!jwt) {
        if (!cancelled) setStatus("fallback");
        setMsg("Please log in first.");
        return;
      }
      try {
        // try the localhost callback receiver
        const r = await fetch("http://127.0.0.1:7462/fofus-cad-callback?jwt=" + encodeURIComponent(jwt) + "&tool=" + tool);
        if (r.ok && !cancelled) {
          setStatus("linked");
          return;
        }
        throw new Error("receiver not reachable");
      } catch {
        if (!cancelled) setStatus("fallback");
      }
    })();
    return () => { cancelled = true; };
  }, [tool]);

  const pair = async () => {
    try {
      const r = await apiClient.post(`/designers/me/presence/pair?tool=${tool}`);
      setMsg("Paste this code in the plugin (valid 10 min): " + (r.data?.pairing_code ?? ""));
    } catch {
      setMsg("Pairing failed");
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <h1 className="text-2xl font-bold text-ink-900">FOFUS CAD Suite</h1>
      {status === "sending" && <p className="mt-3 text-sm text-ink-600">Linking your account to your {tool} plugin…</p>}
      {status === "linked" && (
        <div className="mt-3 rounded-lg bg-blue-50 p-4">
          <p className="text-sm font-semibold text-blue-700">Connected. You can close this tab and return to {tool}.</p>
          <p className="mt-1 text-xs text-ink-500">Your account is now linked to this device. Open the FOFUS panel in your tool to see your status.</p>
        </div>
      )}
      {status === "fallback" && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-ink-600">
            Couldn't reach the plugin automatically. Two options:
          </p>
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="text-xs text-ink-600">1. Log in inside the plugin with your FOFUS email + password.</p>
          </div>
          <div className="rounded-lg bg-blue-50 p-3">
            <p className="text-xs text-ink-600">2. Or pair with a code:</p>
            <button onClick={pair} className="btn-primary mt-2 text-sm">Get pairing code</button>
            {msg && <p className="mt-2 font-mono text-sm text-blue-700">{msg}</p>}
          </div>
          <Link to="/login" className="text-sm font-medium text-blue-700 underline">Go to login</Link>
        </div>
      )}
    </div>
  );
}